import React, { useState, useEffect, useRef } from 'react';
import './Toast.css';

// Toast service - singleton pattern
class ToastService {
  constructor() {
    this.listeners = [];
    this.toasts = [];
  }

  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  notify(toast) {
    const id = Date.now();
    const newToast = { ...toast, id };
    this.toasts.push(newToast);
    this.listeners.forEach(cb => cb([...this.toasts]));

    // Tự động xóa sau duration
    if (toast.duration !== 0) {
      setTimeout(() => {
        this.remove(id);
      }, toast.duration || 3000);
    }

    return id;
  }

  remove(id) {
    this.toasts = this.toasts.filter(t => t.id !== id);
    this.listeners.forEach(cb => cb([...this.toasts]));
  }

  // Helper methods
  success(message, duration = 3000) {
    return this.notify({ type: 'success', message, duration });
  }

  error(message, duration = 4000) {
    return this.notify({ type: 'error', message, duration });
  }

  info(message, duration = 3000) {
    return this.notify({ type: 'info', message, duration });
  }

  warning(message, duration = 3000) {
    return this.notify({ type: 'warning', message, duration });
  }

  // Confirmation dialog
  confirm(message, onConfirm, onCancel) {
    // Xóa tất cả toast confirm cũ trước khi thêm mới (chỉ cho phép 1 confirm dialog)
    this.toasts = this.toasts.filter(t => t.type !== 'confirm');
    
    const id = Date.now();
    const confirmToast = {
      id,
      type: 'confirm',
      message,
      duration: 0, // Không tự động đóng
      onConfirm: () => {
        if (onConfirm) onConfirm();
        this.remove(id);
      },
      onCancel: () => {
        if (onCancel) onCancel();
        this.remove(id);
      }
    };
    this.toasts.push(confirmToast);
    this.listeners.forEach(cb => cb([...this.toasts]));
    console.log('✅ Confirm toast added, total toasts:', this.toasts.length);
    return id;
  }
}

export const toastService = new ToastService();

// Toast Container Component
const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const unsubscribe = toastService.subscribe((newToasts) => {
      console.log('📢 Toast state updated:', newToasts.length, 'toasts');
      setToasts(newToasts);
    });
    return unsubscribe;
  }, []);

  if (toasts.length === 0) return null;

  console.log('🎨 ToastContainer rendering', toasts.length, 'toasts');

  return (
    <div className="toast-container" style={{ zIndex: 10001 }}>
      {toasts.map(toast => {
        console.log('🎨 Rendering toast:', toast.id, 'type:', toast.type);
        return <Toast key={toast.id} toast={toast} />;
      })}
    </div>
  );
};

// Individual Toast Component
const Toast = ({ toast }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const toastRef = useRef(null);

  useEffect(() => {
    // Trigger animation ngay sau khi mount
    const timer = setTimeout(() => {
      setIsVisible(true);
      console.log('🎨 Toast animation triggered for toast:', toast.id, 'type:', toast.type);
    }, 10);

    return () => {
      clearTimeout(timer);
    };
  }, [toast.id, toast.type]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      toastService.remove(toast.id);
    }, 300);
  };

  // Confirmation dialog
  if (toast.type === 'confirm') {
    return (
      <div 
        ref={toastRef}
        className={`toast toast-confirm ${isVisible ? 'show' : ''} ${isLeaving ? 'leaving' : ''}`}
        style={{ 
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateX(0)' : 'translateX(400px)'
        }}
      >
        <div className="toast-content">
          <div className="toast-message">{toast.message}</div>
        </div>
        <div className="toast-actions">
          <button 
            className="toast-btn toast-btn-cancel" 
            onClick={toast.onCancel || handleClose}
          >
            Hủy
          </button>
          <button 
            className="toast-btn toast-btn-confirm" 
            onClick={toast.onConfirm || handleClose}
          >
            Xác nhận
          </button>
        </div>
      </div>
    );
  }

  // Regular toast
  const icons = {
    success: '',
    error: '',
    warning: '',
    info: 'ℹ',
  };

  return (
    <div 
      ref={toastRef}
      className={`toast toast-${toast.type} ${isVisible ? 'show' : ''} ${isLeaving ? 'leaving' : ''}`}
      style={{ 
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateX(0)' : 'translateX(400px)'
      }}
    >
      <div className="toast-content">
        <div className="toast-icon">{icons[toast.type] || icons.info}</div>
        <div className="toast-message">{toast.message}</div>
        <button className="toast-close" onClick={handleClose}>×</button>
      </div>
    </div>
  );
};

export default ToastContainer;

